import { useIsomorphicLayoutEffect } from "@tamagui/constants";
import { useDidFinishSSR } from "@tamagui/use-did-finish-ssr";
import { useForceUpdate } from "@tamagui/use-force-update";
import React, {
  Children,
  cloneElement,
  isValidElement,
  useContext,
  useEffect,
  useRef
} from "react";
import { LayoutGroupContext } from "./LayoutGroupContext";
import { PresenceChild } from "./PresenceChild";
import { Fragment, jsx } from "react/jsx-runtime";
const getChildKey = (child) => child.key || "", isDev = process.env.NODE_ENV !== "production";
function updateChildLookup(children, allChildren) {
  const seenChildren = isDev ? /* @__PURE__ */ new Set() : null;
  children.forEach((child) => {
    const key = getChildKey(child);
    isDev && seenChildren && seenChildren.has(key) && (console.warn(
      `Children of AnimatePresence require unique keys. "${key}" is a duplicate.`
    ), seenChildren.add(key)), allChildren.set(key, child);
  });
}
function onlyElements(children) {
  const filtered = [];
  return Children.forEach(children, (child, index) => {
    isValidElement(child) && (!child.key && Children.count(children) > 1 ? (process.env.NODE_ENV === "development" && console.warn("No key given to AnimatePresence child, assigning index as key"), filtered.push(
      React.cloneElement(child, {
        key: index
      })
    )) : filtered.push(child));
  }), filtered;
}
const AnimatePresence = ({
  children,
  enterVariant,
  exitVariant,
  enterExitVariant,
  initial = !0,
  onExitComplete,
  exitBeforeEnter,
  presenceAffectsLayout = !0
}) => {
  let forceRender = useForceUpdate();
  const isClientMounted = useDidFinishSSR(), forceRenderLayoutGroup = useContext(LayoutGroupContext).forceRender;
  forceRenderLayoutGroup && (forceRender = forceRenderLayoutGroup);
  const isMounted = useRef(!1), filteredChildren = onlyElements(children);
  let childrenToRender = filteredChildren;
  const exiting = /* @__PURE__ */ new Set(), presentChildren = useRef(childrenToRender), allChildren = useRef(/* @__PURE__ */ new Map()).current, isInitialRender = useRef(!0);
  useEffect(() => (isMounted.current = !0, () => {
    isMounted.current = !1, isInitialRender.current = !0, allChildren.clear(), exiting.clear();
  }), []), useIsomorphicLayoutEffect(() => {
    isInitialRender.current = !1, updateChildLookup(filteredChildren, allChildren), presentChildren.current = childrenToRender;
  });
  const hasWarned = process.env.NODE_ENV === "development" ? useRef(!1) : null;
  if (isInitialRender.current)
    return /* @__PURE__ */ jsx(Fragment, { children: childrenToRender.map((child) => /* @__PURE__ */ jsx(
      PresenceChild,
      {
        isPresent: !!(isClientMounted || isMounted.current),
        enterExitVariant,
        exitVariant,
        enterVariant,
        initial: initial ? void 0 : !1,
        presenceAffectsLayout,
        children: child
      },
      getChildKey(child)
    )) });
  childrenToRender = [...childrenToRender];
  const presentKeys = presentChildren.current.map(getChildKey), targetKeys = filteredChildren.map(getChildKey), numPresent = presentKeys.length;
  for (let i = 0; i < numPresent; i++) {
    const key = presentKeys[i];
    targetKeys.indexOf(key) === -1 && exiting.add(key);
  }
  return exitBeforeEnter && exiting.size && (childrenToRender = []), exiting.forEach((key) => {
    if (targetKeys.indexOf(key) !== -1)
      return;
    const child = allChildren.get(key);
    if (!child)
      return;
    const insertionIndex = presentKeys.indexOf(key);
    childrenToRender.splice(
      insertionIndex,
      0,
      /* @__PURE__ */ jsx(
        PresenceChild,
        {
          isPresent: !1,
          onExitComplete: () => {
            allChildren.delete(key), exiting.delete(key);
            const removeIndex = presentChildren.current.findIndex(
              (presentChild) => presentChild.key === key
            );
            if (presentChildren.current.splice(removeIndex, 1), !exiting.size) {
              if (presentChildren.current = filteredChildren, isMounted.current === !1)
                return;
              forceRender(), onExitComplete?.();
            }
          },
          exitVariant,
          enterVariant,
          enterExitVariant,
          presenceAffectsLayout,
          children: child
        },
        getChildKey(child)
      )
    );
  }), childrenToRender = childrenToRender.map((child) => {
    const key = child.key;
    return exiting.has(key) ? child : /* @__PURE__ */ jsx(
      PresenceChild,
      {
        isPresent: !0,
        exitVariant,
        enterVariant,
        enterExitVariant,
        presenceAffectsLayout,
        children: child
      },
      getChildKey(child)
    );
  }), /* @__PURE__ */ jsx(Fragment, { children: exiting.size ? childrenToRender : childrenToRender.map((child) => cloneElement(child)) });
};
AnimatePresence.displayName = "AnimatePresence";
export {
  AnimatePresence
};
//# sourceMappingURL=AnimatePresence.js.map
